import React, { useEffect, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiDollarSign,
  FiClock,
  FiXCircle,
} from "react-icons/fi";
import { getAllTransactions } from "../../../services/service";

interface Transaction {
  id: number;
  user: {
    firstName: string;
    secondName?: string;
  };
  amount: string;
  service: string;
  method: string;
  status: string;
  remainingTime: number;
  createdAt: string;
  duration: number;
  endDate: string;
}

export const Payments = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalFailed, setTotalFailed] = useState(0);
  const [totalPending, setTotalPending] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const transactionsPerPage = 10;

  useEffect(() => {
    const getTransactions = async () => {
      try {
        setLoading(true);
        const response = await getAllTransactions();
        setTransactions(response.data.transactions);
        setTotalRevenue(response.data.totalRevenue);
        setTotalFailed(response.data.totalFailed);
        setTotalPending(response.data.totalPending);
        setLoading(false);
      } catch (error) {
        if (error) {
          setErr("Failed to fetch transactions");
        }
      }
    };
    getTransactions();
  }, []);

  // Calculate days remaining and add subscription duration for each transaction
  const transactionsWithDaysRemaining: Transaction[] = transactions?.map(
    (txn) => {
      const startDate = new Date(txn.createdAt);
      const durationMonths = txn.duration;
      const endDate = new Date(startDate);
      endDate.setMonth(startDate.getMonth() + durationMonths);

      return {
        ...txn,
        endDate: endDate.toISOString().split("T")[0],
        subscriptionDuration: `${durationMonths} month${
          durationMonths > 1 ? "s" : ""
        }`,
      };
    }
  );

  // Pagination logic remains the same
  const indexOfLastTransaction = currentPage * transactionsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
  const currentTransactions = transactionsWithDaysRemaining.slice(
    indexOfFirstTransaction,
    indexOfLastTransaction
  );
  const totalPages = Math.ceil(
    transactionsWithDaysRemaining.length / transactionsPerPage
  );

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  return (
    <div className="p-4 sm:p-6">
      <h2 className="text-2xl font-bold mb-6">Transactions</h2>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
              <FiDollarSign size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Earings</p>
              <p className="text-2xl font-bold">
                {totalRevenue} <span className="text-lg">FRW</span>
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 text-yellow-600 mr-4">
              <FiClock size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold">
                {totalPending} <span className="text-lg">FRW</span>
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 text-red-600 mr-4">
              <FiXCircle size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Failed</p>
              <p className="text-2xl font-bold">
                {totalFailed} <span className="text-lg">FRW</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* All Transactions */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">All Transactions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">
                  <FiCalendar className="inline mr-1" /> Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">
                  Days Left
                </th>
              </tr>
            </thead>
            {loading ? (
              <>
                <div className="flex justify-center items-center">
                  Loading ...
                </div>
              </>
            ) : transactions.length > 0 ? (
              <tbody className="bg-white divide-y divide-gray-200">
                {currentTransactions?.map((txn) => (
                  <tr key={txn.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {txn.createdAt.toString().split("T")[0]}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {txn.user.firstName} {txn.user.secondName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {txn.service}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {txn.amount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {txn.method}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${
                          txn.status === "COMPLETED"
                            ? "bg-green-100 text-green-800"
                            : txn.status === "PENDING"
                            ? "bg-yellow-200 text-yellow-800"
                            : txn.status === "FAILED"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-300"
                        }`}
                      >
                        {txn.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {txn.duration} months
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          txn.remainingTime <= 3
                            ? "bg-red-100 text-red-800"
                            : txn.remainingTime <= 7
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {txn.remainingTime > 30
                          ? `${Math.floor(txn.remainingTime / 30)} month(s) ${
                              txn.remainingTime % 30
                            } day(s)`
                          : `${txn.remainingTime} day(s)`}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : (
              <>
                <div className="flex justify-center items-center">
                  {err ? err : "No Transactions"}
                </div>
              </>
            )}
          </table>
        </div>
        {/* Pagination */}
        {transactionsWithDaysRemaining.length > transactionsPerPage && (
          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Previous
              </button>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">
                    {indexOfFirstTransaction + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(
                      indexOfLastTransaction,
                      transactionsWithDaysRemaining.length
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium">
                    {transactionsWithDaysRemaining.length}
                  </span>{" "}
                  results
                </p>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                      currentPage === 1
                        ? "text-gray-300"
                        : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <span className="sr-only">Previous</span>
                    <FiChevronLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (number) => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === number
                            ? "z-10 bg-yellow-50 border-yellow-500 text-yellow-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {number}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                      currentPage === totalPages
                        ? "text-gray-300"
                        : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <span className="sr-only">Next</span>
                    <FiChevronRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
